import type { MouseEvent } from 'react';
import type { IHeader, ISwimlaneActions } from '@/components/ui/swimlane';
import {
  ColumnHeader as SwimlaneColumnHeader,
  RowHeader as SwimlaneRowHeader,
} from '@/components/ui/swimlane';
import {
  DEFAULT_FALLBACK_COLOR,
  getContrastTextColor,
} from '@/lib/utils/loop-visualization/colors';

/**
 * Extract headerColor from header props at runtime.
 *
 * The swimlane component's IHeader type only includes { id, label, isDateHeader },
 * but ISwimlaneHeaderProps (passed as columnHeaders/rowHeaders) includes headerColor.
 * Since the swimlane passes IHeader to slot components, we need runtime checking
 * to safely access the headerColor property that exists on the actual data.
 */
export const resolveHeaderColor = (header: IHeader): string | undefined => {
  const headerWithColor = header as IHeader & { headerColor?: string };
  return headerWithColor.headerColor;
};

export function ReadOnlyColumnHeader(props: IHeader) {
  const backgroundColor = resolveHeaderColor(props) || DEFAULT_FALLBACK_COLOR;
  const labelColor = getContrastTextColor(backgroundColor);

  return (
    <SwimlaneColumnHeader
      {...props}
      backgroundColor={backgroundColor}
      labelColor={labelColor}
      isViewCardOptionDisabled={true}
      isReOrderingDisabled={true}
      onOpenCard={() => {}}
      hideHeader={() => {}}
      openCardLabel=""
    >
      {props.label || ''}
    </SwimlaneColumnHeader>
  );
}

function ReadOnlyRowHeaderBase(props: IHeader & { isGroupHeader?: boolean }) {
  const backgroundColor = resolveHeaderColor(props) || DEFAULT_FALLBACK_COLOR;
  const labelColor = getContrastTextColor(backgroundColor);

  return (
    <SwimlaneRowHeader
      {...props}
      backgroundColor={backgroundColor}
      labelColor={labelColor}
      isViewCardOptionDisabled={true}
      isReOrderingDisabled={true}
      onOpenCard={() => {}}
      hideHeader={() => {}}
      openCardLabel=""
    >
      {props.label || ''}
    </SwimlaneRowHeader>
  );
}

export function ReadOnlyRowHeader(props: IHeader) {
  return <ReadOnlyRowHeaderBase {...props} />;
}

export function ReadOnlyGroupRowHeader(props: IHeader) {
  return <ReadOnlyRowHeaderBase {...props} isGroupHeader={true} />;
}

export const NO_OP_ACTIONS: Readonly<ISwimlaneActions> = {
  headerActions: {
    addHiddenHeaderValue: (_level: 1 | 2, _event: MouseEvent<HTMLButtonElement>) => {},
    setRowHeaderWidth: (_width: number) => {},
  },
  itemCardActions: {
    addLinkedItemHandler: (_itemID: string) => {},
    addSubItemHandler: (_itemID: string) => {},
    copyItemURLHandler: (_itemID: string) => {},
    deleteItemHandler: (_itemID: string) => {},
    duplicateItemHandler: (_itemID: string) => {},
    renameItemHandler: (_itemID: string) => {},
    transferItemHandler: (_itemID: string) => {},
  },
  itemButtonActions: {
    attachmentsHandler: (_itemID: string) => {},
    commentsHandler: (_itemID: string) => {},
    keyDatesHandler: (_itemID: string) => {},
    linkedItemsHandler: (_itemID: string) => {},
  },
} as const;
